import AzureDevopsService from 'src/app/shared/services/azuredevops.service';
import { IProfile } from './../interfaces/azure/userprofile.interface';
import {
  IWebAppSettingsResponse,
  IWebAppSettingsPayload,
} from './../interfaces/azure/webappsettings.interface';
import { Injectable } from '@angular/core';
import { 
  filter,
  map,
  mergeMap,
  Observable,  
  from,
  toArray,
  of,
  catchError,
  tap,
  throwError,
} from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { IAzureWebApp } from '../interfaces/azure/webapp.interface';
import { IPullRequest } from '../interfaces/azuredevops/pullrequest.interface';
import { AzureBlobUploadPayload } from '../interfaces/azure/blob-upload.interface';

@Injectable({
  providedIn: 'root',
})
export default class AzureService {
  constructor(private httpClient: HttpClient, private azureDevopsService: AzureDevopsService) {}

  public triggerLogicApp<T>(endpoint: string, body: T): Observable<any> {
    return this.httpClient.post(endpoint, body);
  }

  public uploadBlob(payload: AzureBlobUploadPayload) {
    const headers = new HttpHeaders({
      'x-ms-date': new Date().toUTCString(),
      'Content-Type': payload.file.type,
      'x-ms-blob-type': 'BlockBlob',
      'x-ms-version': '2017-11-09'
    });

    return this.httpClient.put(`${payload.storageEndpoint}/${payload.container}/${payload.blobName}`, payload.file, {
      headers
    }).pipe(
      map(_ => {
        return { fileLink: `${payload.storageEndpoint}/${payload.container}/${payload.blobName}` }
      })
    )
  }

  public getBlob(container: string, blobName: string) {
    const headers = new HttpHeaders({
      'x-ms-date': new Date().toUTCString(),
      'x-ms-blob-type': 'BlockBlob',
      'x-ms-version': '2017-11-09'
    });

    return this.httpClient.get(`${environment.azure.whitelabel.storageEndpoint}/${container}/${blobName}`, {
      headers,
      responseType: 'arraybuffer'
    })
  }

  public getBlobFolder(container: string, blobName: string): Observable<string | null> {
    if (blobName.trim() === '') return new Observable(s => s.next(null)); 

    const headers = new HttpHeaders({
      'x-ms-date': new Date().toUTCString(),
      'x-ms-blob-type': 'BlockBlob',
      'x-ms-version': '2017-11-09'
    });

    return this.httpClient.get(`${environment.azure.whitelabel.storageEndpoint}/${container}?restype=container&comp=list&prefix=${blobName}`, {
      headers,
      responseType: 'text'
    })
  }

  public getWebAppsWithPulumi(
    repositoryName: string,
    repositoryId: string
  ): Observable<IAzureWebApp[]> {
    return this.httpClient
      .get<{ value: IAzureWebApp[] }>(
        `https://management.azure.com/subscriptions/${environment.azure.subscriptionId}/resources?api-version=2021-04-01&$filter=tagName eq 'Pulumi'`,
        {}
      )
      .pipe(
        map((val) => {
          return val.value.filter((app) => {
            return app.name!.includes(`${repositoryName}-`);
          });
        }),
        mergeMap((filteredWebApps) => {
          return from(filteredWebApps);
        }),
        mergeMap((filteredWebApp) => {
          return this.azureDevopsService
            .getPullRequestInformations(
              repositoryId,
              this.extractPullRequestId(filteredWebApp.name!)
            )
            .pipe(
              catchError((error, foo) => {
                if (error.status === 404) {
                  return of(null);
                } else {
                  throw error;
                }
              }),
              mergeMap<IPullRequest | null, Observable<IAzureWebApp>>(
                (pullRequest: IPullRequest | null) => {
                  return of<IAzureWebApp>({
                    name: filteredWebApp.name,
                    pullRequestAuthor: pullRequest?.createdBy.displayName,
                    pullRequestId: pullRequest?.pullRequestId,
                    pullRequestTitle: pullRequest?.title,
                    pullRequestValid: pullRequest?.status,
                  });
                }
              )
            );
        }),
        toArray(),
        map((webapps: IAzureWebApp[]) => {
          return webapps.sort((firstWebApp, secondWebApp) => {
            if (
              this.extractPullRequestId(firstWebApp.name!) >
              this.extractPullRequestId(secondWebApp.name!)
            )
              return -1;
            return 1;
          });
        })
      );
  }

  public updateWebAppSettings(webAppName: string, appSettings: Array<{ [key: string]: string }>) {
    return this.withResourceGroupFallback((resourceGroupName) => {
      return this.httpClient.put(
        this.buildAppSettingsUrl(resourceGroupName, webAppName, 'set'),
        {
          properties: this.normalizeAppSettingsToPost(appSettings),
        }
      );
    });
  }

  public getWebAppSettings(webAppName: string): Observable<{ [key: string]: string }> {
    return this.withResourceGroupFallback((resourceGroupName) => {
      return this.httpClient.post<IWebAppSettingsResponse>(
          this.buildAppSettingsUrl(resourceGroupName, webAppName, 'list'),
          {}
        )
        .pipe(
          map((res) => {
            return res.properties;
          })
        );
    });
  }

  public getUserProfile(): Observable<IProfile> {
    return this.httpClient.get<IProfile>(
      'https://graph.microsoft.com/v1.0/me'
    );
  }

  public getWebAppsByContext(context: string, kind: 'functionapp' | 'app' = 'app'): Observable<IAzureWebApp[]> {    
    return this.httpClient.get<{ [value: string]: IAzureWebApp[] }>(
        `https://management.azure.com/subscriptions/${environment.azure.subscriptionId}/resources?api-version=2021-04-01&$filter=tagName eq 'Context' and tagValue eq '${context}'`
      )      
      .pipe(
        map((webapps) => {
          return webapps['value'].filter((webapp) => webapp.kind === kind)
            .map<IAzureWebApp>((webapp) => {
              return {
                id: webapp.id,
                name: webapp.name,
                properties: webapp.properties,
                type: webapp.type,
                kind: webapp.kind
              };
            })
        })
      );
      
  }

  private normalizeAppSettingsToPost(appSettings: Array<{ [key: string]: string }>) {
    const normalizedPayload: { [key: string]: string } = {};

    appSettings.forEach((element: { [key: string]: string }) => {
      normalizedPayload[element['key']] = element['value'];
    });
    return normalizedPayload;
  }

  private getResourceGroupNames(): string[] {
    const primary = environment.azure.environments.testServer.resourceGroupName;
    const fallback = environment.azure.environments.pulumi?.resourceGroupName;

    if (!fallback || fallback === primary) {
      return [primary];
    }

    return [primary, fallback];
  }

  private buildAppSettingsUrl(
    resourceGroupName: string,
    webAppName: string,
    action: 'list' | 'set'
  ): string {
    const baseUrl = `https://management.azure.com/subscriptions/${environment.azure.subscriptionId}/resourceGroups/${resourceGroupName}/providers/Microsoft.Web/sites/${webAppName}/config/appsettings`;
    const suffix = action === 'list' ? '/list' : '';

    return `${baseUrl}${suffix}?api-version=2021-02-01`;
  }

  private withResourceGroupFallback<T>(
    requestFactory: (resourceGroupName: string) => Observable<T>
  ): Observable<T> {
    const resourceGroups = this.getResourceGroupNames();

    if (resourceGroups.length === 1) {
      return requestFactory(resourceGroups[0]);
    }

    return requestFactory(resourceGroups[0]).pipe(
      catchError((error) => {
        
          return requestFactory(resourceGroups[1]);
        
      })
    );
  }

  private extractPullRequestId(webappName: string): number {
    return Number(webappName.split('-')[1]);
  }
}
